/**
 * Dokumentum változók típusai és kezelése
 */

export type VariableType = 
  | 'text'           // Szöveges mező
  | 'number'         // Szám
  | 'date'           // Dátum
  | 'email'          // Email cím
  | 'phone'          // Telefonszám
  | 'currency'       // Pénzösszeg
  | 'percentage'     // Százalék
  | 'boolean'        // Igen/Nem
  | 'select'         // Legördülő lista
  | 'textarea';      // Többsoros szöveg

export type VariableScope = 
  | 'global'         // Minden sablonban elérhető
  | 'template'       // Csak adott sablonban
  | 'organization'   // Szervezet szintű
  | 'customer';      // Ügyfél specifikus

export interface DocumentVariable {
  id: string;
  name: string;                    // Megjelenített név
  key: string;                     // Sablon kulcs (pl. {{COMPANY_NAME}})
  type: VariableType;
  scope: VariableScope;
  
  // Opcionális beállítások
  description?: string;            // Leírás
  defaultValue?: string;           // Alapértelmezett érték
  required?: boolean;              // Kötelező mező
  placeholder?: string;            // Placeholder szöveg
  
  // Select típushoz
  options?: string[];              // Legördülő lista opciók
  
  // Validáció
  validation?: {
    min?: number;                  // Minimum érték/hossz
    max?: number;                  // Maximum érték/hossz
    pattern?: string;              // Regex minta
    message?: string;              // Hibaüzenet
  };
  
  // Metaadatok
  organizationId: string;
  templateId?: string;             // Ha template scope
  customerId?: string;             // Ha customer scope
  category?: string;               // Kategória (pl. 'company', 'contact', 'financial')
  order?: number;                  // Sorrend
  isActive: boolean;
  
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export interface VariableValue {
  id: string;
  variableId: string;
  value: string;
  
  // Kontextus
  templateId?: string;
  customerId?: string;
  documentId?: string;
  
  organizationId: string;
  createdAt: Date;
  updatedAt: Date;
}

// Alapértelmezett változó kategóriák
export const DEFAULT_VARIABLE_CATEGORIES = [
  { id: 'company', name: 'Cég adatok', icon: 'BuildingOfficeIcon', color: '#3b82f6' },
  { id: 'contact', name: 'Kapcsolattartó', icon: 'UserIcon', color: '#10b981' },
  { id: 'financial', name: 'Pénzügyi', icon: 'CurrencyDollarIcon', color: '#f59e0b' },
  { id: 'legal', name: 'Jogi', icon: 'ScaleIcon', color: '#8b5cf6' },
  { id: 'project', name: 'Projekt', icon: 'FolderIcon', color: '#ef4444' },
  { id: 'dates', name: 'Dátumok', icon: 'CalendarIcon', color: '#06b6d4' },
  { id: 'custom', name: 'Egyedi', icon: 'CogIcon', color: '#6b7280' }
] as const;

// Perfect Group alapértelmezett változók
export const DEFAULT_DOCUMENT_VARIABLES: Omit<DocumentVariable, 'id' | 'organizationId' | 'createdAt' | 'updatedAt' | 'createdBy'>[] = [
  // Cég adatok
  {
    name: 'Cég megnevezése',
    key: 'COMPANY_NAME',
    type: 'text',
    scope: 'global',
    category: 'company',
    required: true,
    order: 1,
    isActive: true
  },
  {
    name: 'Cég címe',
    key: 'COMPANY_ADDRESS',
    type: 'textarea',
    scope: 'global',
    category: 'company',
    order: 2,
    isActive: true
  },
  {
    name: 'Adószám',
    key: 'TAX_NUMBER',
    type: 'text',
    scope: 'global',
    category: 'company',
    validation: { pattern: '^[0-9]{8}-[0-9]-[0-9]{2}$', message: 'Adószám formátum: 12345678-1-23' },
    order: 3,
    isActive: true
  },
  {
    name: 'Cégjegyzékszám',
    key: 'REGISTRATION_NUMBER',
    type: 'text',
    scope: 'global',
    category: 'company',
    order: 4,
    isActive: true
  },
  
  // Kapcsolattartó adatok
  {
    name: 'Kapcsolattartó neve',
    key: 'CONTACT_NAME',
    type: 'text',
    scope: 'global',
    category: 'contact',
    order: 10,
    isActive: true
  },
  {
    name: 'Kapcsolattartó email',
    key: 'CONTACT_EMAIL',
    type: 'email',
    scope: 'global',
    category: 'contact',
    order: 11,
    isActive: true
  },
  {
    name: 'Kapcsolattartó telefon',
    key: 'CONTACT_PHONE',
    type: 'phone',
    scope: 'global',
    category: 'contact',
    order: 12,
    isActive: true
  },
  
  // Pénzügyi adatok
  {
    name: 'Bankszámlaszám',
    key: 'BANK_ACCOUNT',
    type: 'text',
    scope: 'global',
    category: 'financial',
    order: 20,
    isActive: true
  },
  {
    name: 'Fizetési határidő (nap)',
    key: 'PAYMENT_TERMS',
    type: 'number',
    scope: 'global',
    category: 'financial',
    defaultValue: '30',
    validation: { min: 1, max: 365 },
    order: 21,
    isActive: true
  },
  
  // Dátumok
  {
    name: 'Mai dátum',
    key: 'CURRENT_DATE',
    type: 'date',
    scope: 'global',
    category: 'dates',
    description: 'Automatikusan kitöltődik a mai dátummal',
    order: 30,
    isActive: true
  },
  {
    name: 'Szerződés kezdete',
    key: 'CONTRACT_START_DATE',
    type: 'date',
    scope: 'template',
    category: 'dates',
    order: 31,
    isActive: true
  },
  {
    name: 'Szerződés vége',
    key: 'CONTRACT_END_DATE',
    type: 'date',
    scope: 'template',
    category: 'dates',
    order: 32,
    isActive: true
  },
  
  // Jogi
  {
    name: 'Képviselő neve',
    key: 'REPRESENTATIVE_NAME',
    type: 'text',
    scope: 'global',
    category: 'legal',
    order: 40,
    isActive: true
  },
  
  // Projekt specifikus
  {
    name: 'Projekt neve',
    key: 'PROJECT_NAME',
    type: 'text',
    scope: 'template',
    category: 'project',
    order: 50,
    isActive: true
  },
  {
    name: 'Projekt leírása',
    key: 'PROJECT_DESCRIPTION',
    type: 'textarea',
    scope: 'template',
    category: 'project',
    order: 51,
    isActive: true
  }
];

// Változó érték formázók
export const formatVariableValue = (variable: DocumentVariable, value: string): string => {
  if (!value) return '';
  
  switch (variable.type) {
    case 'currency':
      const num = parseFloat(value);
      return isNaN(num) ? value : new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: 'HUF'
      }).format(num);
      
    case 'percentage':
      const pct = parseFloat(value);
      return isNaN(pct) ? value : `${pct}%`;
      
    case 'date':
      const date = new Date(value);
      return isNaN(date.getTime()) ? value : date.toLocaleDateString('hu-HU');
      
    case 'phone':
      // Magyar telefonszám formázás
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length === 11 && cleaned.startsWith('36')) {
        return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
      }
      return value;
      
    default:
      return value;
  }
};

// Változó validáció
export const validateVariableValue = (variable: DocumentVariable, value: string): string | null => {
  if (variable.required && !value.trim()) {
    return `${variable.name} mező kitöltése kötelező`;
  }
  
  if (!value) return null;
  
  const { validation } = variable;
  if (!validation) return null;
  
  // Hossz ellenőrzés
  if (validation.min && value.length < validation.min) {
    return `${variable.name} minimum ${validation.min} karakter hosszú legyen`;
  }
  
  if (validation.max && value.length > validation.max) {
    return `${variable.name} maximum ${validation.max} karakter hosszú lehet`;
  }
  
  // Pattern ellenőrzés
  if (validation.pattern) {
    const regex = new RegExp(validation.pattern);
    if (!regex.test(value)) {
      return validation.message || `${variable.name} formátuma nem megfelelő`;
    }
  }
  
  // Típus specifikus validáció
  switch (variable.type) {
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return 'Érvényes email címet adjon meg';
      }
      break;
      
    case 'number':
      const num = parseFloat(value);
      if (isNaN(num)) {
        return 'Érvényes számot adjon meg';
      }
      if (validation.min && num < validation.min) {
        return `Minimum érték: ${validation.min}`;
      }
      if (validation.max && num > validation.max) {
        return `Maximum érték: ${validation.max}`;
      }
      break;
  }
  
  return null;
};
